import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useAuth } from "../App";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const PRIV_ROLES = ["admin", "administrador", "superadmin", "supervisor"];

const AtencionPrioritariaContext = createContext(null);

// Normalización equivalente a la del backend (unidecode + lower + colapsar espacios).
export function normalizarNombre(nombre) {
  return (nombre || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

export function AtencionPrioritariaProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [mapa, setMapa] = useState({}); // nombre_norm -> record
  const [alerta, setAlerta] = useState(null); // record activo a mostrar
  const dismissedRef = useRef(new Set()); // normas descartadas en este módulo (ruta)

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const puedeMarcar = !!user && PRIV_ROLES.includes((user.role || "").toLowerCase());

  // Un aviso por MÓDULO del cliente: al cambiar de ruta (módulo) se reinicia el
  // set de descartados y se oculta cualquier alerta pendiente, de modo que el
  // mismo cliente vuelva a alertar en el nuevo módulo (y al re-entrar a uno).
  useEffect(() => {
    dismissedRef.current = new Set();
    setAlerta(null);
  }, [location.pathname]);

  const cargar = useCallback(async () => {
    if (!user || !token) return;
    try {
      const r = await axios.get(`${API}/atencion-prioritaria`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const m = {};
      (r.data || []).forEach((d) => { m[d.nombre_norm] = d; });
      setMapa(m);
    } catch (e) {
      // silencioso: la alerta es no crítica
    }
  }, [user, token]);

  useEffect(() => { cargar(); }, [cargar]);

  const getRecord = useCallback((nombre) => {
    const norm = normalizarNombre(nombre);
    if (!norm) return null;
    return mapa[norm] || null;
  }, [mapa]);

  const esPrioritario = useCallback((nombre) => !!getRecord(nombre), [getRecord]);

  // Llamado por las páginas al abrir/seleccionar un cliente.
  const notificarCliente = useCallback((nombre) => {
    const rec = getRecord(nombre);
    if (!rec) return;
    const norm = rec.nombre_norm;
    if (dismissedRef.current.has(norm)) return;
    setAlerta((prev) => (prev && prev.nombre_norm === norm ? prev : rec));
  }, [getRecord]);

  const cerrarAlerta = useCallback(() => {
    setAlerta((prev) => {
      if (prev) dismissedRef.current.add(prev.nombre_norm);
      return null;
    });
  }, []);

  const marcar = useCallback(async (nombre, motivo = "") => {
    const r = await axios.post(`${API}/atencion-prioritaria`, { nombre, motivo }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    await cargar();
    return r.data;
  }, [token, cargar]);

  const desmarcar = useCallback(async (nombre) => {
    const r = await axios.delete(`${API}/atencion-prioritaria`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { nombre },
    });
    // permitir que vuelva a alertar si se re-marca
    dismissedRef.current.delete(normalizarNombre(nombre));
    await cargar();
    return r.data;
  }, [token, cargar]);

  return (
    <AtencionPrioritariaContext.Provider
      value={{ mapa, alerta, getRecord, esPrioritario, notificarCliente, cerrarAlerta, marcar, desmarcar, recargar: cargar, puedeMarcar }}
    >
      {children}
    </AtencionPrioritariaContext.Provider>
  );
}

export function useAtencionPrioritaria() {
  const ctx = useContext(AtencionPrioritariaContext);
  if (!ctx) {
    // fallback no-op si se usa fuera del provider
    return {
      mapa: {}, alerta: null, getRecord: () => null, esPrioritario: () => false,
      notificarCliente: () => {}, cerrarAlerta: () => {}, marcar: async () => {},
      desmarcar: async () => {}, recargar: async () => {}, puedeMarcar: false,
    };
  }
  return ctx;
}

// Componente watcher: las páginas lo renderizan donde tienen un nombre de cliente
// activo. Dispara la alerta al montarse o al cambiar el nombre.
export function ClienteAtencionWatcher({ nombre }) {
  const { notificarCliente } = useAtencionPrioritaria();
  useEffect(() => {
    if (nombre) notificarCliente(nombre);
  }, [nombre, notificarCliente]);
  return null;
}
